import type { BarSeriesOption, LineSeriesOption, ScatterSeriesOption } from 'echarts/charts';
import { BarChart, CustomChart, LineChart, ScatterChart } from 'echarts/charts';
import {
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { init, registerTheme, use } from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import _merge from 'lodash/merge';
import moment from 'moment-timezone';
import { getColor } from 'volta/utils/style-utils';

import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';

import { volta as voltaTheme } from './theme';

import type { GridComponentOption, TitleComponentOption } from 'echarts/components';
import type { ComposeOption, ECharts, SetOptionOpts } from 'echarts/core';
import type {
  CustomSeriesOption,
  CustomSeriesRenderItem,
  CustomSeriesRenderItemAPI,
  CustomSeriesRenderItemParams,
  CustomSeriesRenderItemReturn
} from 'echarts';
import type AppNavState from 'volta/services/app-nav-state';
import type IntlService from 'ember-intl/services/intl';
// Register the required components
use([
  DatasetComponent,
  LegendComponent,
  ScatterChart,
  LineChart,
  BarChart,
  CustomChart,
  GridComponent,
  TooltipComponent,
  DataZoomComponent, // Used in Line Graph Charts
  SVGRenderer, // If you only need to use the canvas rendering mode, the bundle will not include the SVGRenderer module, which is not needed.
  MarkLineComponent,
  TitleComponent
]);

// Register volta theme
registerTheme('volta-light', voltaTheme('light'));
registerTheme('volta-dark', voltaTheme('dark'));

export interface ISelectLegendArgs {
  name: string;
  selected: Record<string, boolean>;
  type: string;
}

// Combine an Option type with only required components and charts via ComposeOption
export type EChartsOption = ComposeOption<
  | BarSeriesOption
  | LineSeriesOption
  | CustomSeriesOption
  | TitleComponentOption
  | GridComponentOption
  | ScatterSeriesOption
>;

export interface IArgs {
  option: EChartsOption;
  settings?: SetOptionOpts;
  loading?: boolean;
  isEmpty?: boolean;
  theme?: 'light' | 'dark';
  onClick?: (dataset: Record<string, unknown>, event?: MouseEvent) => void;
  onSeriesToggle?: (seriesName: string) => void;
  onZoom?: (event: TZoomEvent) => void;
  onLegendSelect?: (selectArgs: ISelectLegendArgs) => void;
}

export type TParsedValue = number | string | Date;

export type TNumberValue = number | '-' | null | undefined;

export function formatValue(
  value: number | undefined,
  abs?: boolean,
  mapFn?: (value?: number) => TNumberValue
): TNumberValue {
  const result = isPresent(value) ? (abs ? Math.abs(value!) : value!) : undefined;
  return typeof mapFn === 'function' ? mapFn(result) : result;
}

export function baseDataZoom(defaultValue?: TZoomEvent) {
  const { start = 0, end = 100, startValue, endValue, minValueSpan, labelFormatter } = defaultValue ?? {};
  return [
    {
      id: 'mainX',
      type: 'slider',
      realtime: true,
      minValueSpan: minValueSpan ?? 3600 * 24 * 1000 * 30,
      right: 5,
      labelFormatter: labelFormatter,
      start,
      end,
      startValue,
      endValue
    }
  ];
}

export function CustomLineStyleRender(
  fn: (
    series: { id?: string; name?: string; index: number },
    xStart: TParsedValue,
    yStart: TParsedValue,
    xEnd: TParsedValue,
    yEnd: TParsedValue,
    currentSeriesIndices: number[]
  ) => any
): CustomSeriesRenderItem {
  return (params: CustomSeriesRenderItemParams, api: CustomSeriesRenderItemAPI) => {
    const xStart = api.value(0, params.dataIndexInside);
    const xEnd = api.value(0, params.dataIndexInside + 1);
    const yStart = api.value(1, params.dataIndexInside);
    const yEnd = api.value(1, params.dataIndexInside + 1);

    const coord1 = api.coord([xStart, yStart]),
      coord2 = api.coord([xEnd, yEnd]);

    const currentSeriesIndices = api.currentSeriesIndices();
    const series = { id: params.seriesId, name: params.seriesName, index: params.seriesIndex };

    const x1 = coord1[0];
    const y1 = coord1[1];
    const x2 = coord2[0];
    const y2 = coord2[1];
    const baseStyle = {
      stroke: api.visual('color'),
      lineWidth: 2
    };

    return {
      name: series.name,
      type: 'line',
      shape: { x1, y1, x2, y2 },
      transition: 'shape',
      style: {
        ...baseStyle,
        ...(fn(series, xStart, yStart, xEnd, yEnd, currentSeriesIndices) ?? {})
      }
    } as CustomSeriesRenderItemReturn;
  };
}

export default class BvChart extends Component<IArgs> {
  chartRef!: HTMLElement;

  @service appNavState!: AppNavState;
  @service intl!: IntlService;

  chart?: ECharts;

  get theme() {
    const theme = this.args.theme ?? this.appNavState.appliedTheme;
    return `volta-${theme}`;
  }

  get baseOptions(): EChartsOption {
    return {
      xAxis: {
        type: 'time',
        splitLine: {
          show: false
        },
        axisLine: { onZero: false }
      },
      yAxis: {
        type: 'value',
        splitNumber: 4,
        splitLine: {
          show: false
        },
        axisLabel: {
          formatter: (value: number, _index: number) => {
            return isPresent(value) ? this.intl.formatNumber(value) : '';
          }
        }
      },
      dataZoom: [
        {
          type: 'inside'
        }
      ],
      tooltip: {
        trigger: 'axis',
        appendToBody: true,
        valueFormatter: (value: number) => {
          return isPresent(value) ? this.intl.formatNumber(value) : '';
        },
        axisPointer: {
          label: {
            formatter: (args: { value: moment.MomentInput }) => {
              return moment.utc(args.value).format('L');
            }
          }
        }
      },
      series: []
    } as EChartsOption;
  }

  handleInsert = (el: HTMLElement) => {
    this.chartRef = el;
    this.initChart();
  };

  handleUpdateOptions = () => {
    if (this.args.isEmpty) {
      this.chart?.setOption(
        {
          title: {
            textStyle: {
              color: getColor('gray'),
              fontSize: 20
            },
            text: this.intl.t('noDataToDisplay'),
            left: 'center',
            top: 'center'
          }
        },
        true
      );
    } else {
      const opts = _merge({}, this.baseOptions, this.args.option) as EChartsOption;
      this.chart?.setOption(opts, true);
    }
  };

  handleUpdateLoading = () => {
    if (this.args.loading === true) {
      this.chart?.showLoading();
    } else {
      this.chart?.hideLoading();
    }
  };

  handleUpdateTheme = () => {
    this.initChart();
  };

  handleResize = () => {
    this.chart?.resize();
  };

  handleDestroy = () => {
    this.chart?.dispose();
  };

  private initChart() {
    if (this.chartRef) {
      this.chart?.dispose();
      this.chart = init(this.chartRef, this.theme);
      this.chart?.setOption(this.baseOptions, this.args.settings);

      const { onClick, onLegendSelect, onZoom } = this.args;

      if (typeof onClick === 'function') {
        this.chart.on('click', ({ dimensionNames = [], value = [], event }) => {
          const dataset = dimensionNames.reduce((acc: Record<string, unknown>, d, index) => {
            acc[d] = (value as unknown[])[index];
            return acc;
          }, {});

          onClick(dataset, event as MouseEvent | undefined);
        });
      }

      if (typeof onLegendSelect === 'function') {
        this.chart.on('legendselectchanged', ({ name, selected, type }) => {
          onLegendSelect({ name, selected, type });
        });
      }

      if (typeof onZoom === 'function') {
        this.chart.on('datazoom', (event: TZoomEvent) => {
          onZoom(event);
        });
      }

      this.handleUpdateOptions();
      this.handleUpdateLoading();
    }
  }
}
